import { useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ChevronRight,
  Diamond,
  FolderOpen,
  Calendar,
  MapPin,
} from 'lucide-react';
import { api } from '@/lib/api/client';
import { brandsApi } from '@/features/brands/api';
import { queryKeys } from '@/lib/api/queryKeys';
import { useSelectedBrandId } from '@/lib/branding/brandContextStore';
import { resolveImageUrl } from '@/lib/images/resolveImageUrl';
import { useT } from '@/lib/i18n/useT';
import type { XkEvent, XkEventGroup, XkStation } from '@/lib/api/types';
import { cn } from '@/lib/cn';

/**
 * Brand-scoped tree shown in the Sidebar above the global nav. Renders the
 * currently selected brand plus an accordion of its projects (event groups)
 * and their events.
 *
 * Single-open behavior: only one project can be expanded at a time, and
 * within that project only one event can be expanded — opening another
 * collapses the previous one.
 *
 * Returns null when the sidebar is collapsed or no brand is selected; the
 * collapsed sidebar already shows the global nav as icons.
 */
export function SidebarBrandTree({ expanded }: { expanded: boolean }) {
  const t = useT();
  const location = useLocation();
  const brandId = useSelectedBrandId();
  const [expandedProjectId, setExpandedProjectId] = useState<number | null>(null);
  const [expandedEventId, setExpandedEventId] = useState<number | null>(null);

  const brandQ = useQuery({
    queryKey: queryKeys.brands.detail(brandId ?? 0),
    queryFn: () => brandsApi.get(brandId!),
    enabled: !!brandId,
  });
  const groupsQ = useQuery({
    queryKey: queryKeys.eventGroups.byBrand(brandId ?? 0),
    queryFn: () =>
      api.list<XkEventGroup>('/xk-event-group', { filter: { companyId: brandId } }),
    enabled: !!brandId,
  });
  const eventsQ = useQuery({
    queryKey: queryKeys.events.byBrand(brandId ?? 0),
    queryFn: () =>
      api.list<XkEvent>('/xk-event', { filter: { companyId: brandId } }),
    enabled: !!brandId,
  });
  const stationsQ = useQuery({
    queryKey: queryKeys.stations.byBrand(brandId ?? 0),
    queryFn: () =>
      api.list<XkStation>('/xk-station', { filter: { companyId: brandId } }),
    enabled: !!brandId,
  });

  const eventsByGroup = useMemo(() => {
    const map = new Map<number, XkEvent[]>();
    for (const e of eventsQ.data?.items ?? []) {
      const list = map.get(e.eventGroupId) ?? [];
      list.push(e);
      map.set(e.eventGroupId, list);
    }
    return map;
  }, [eventsQ.data]);
  const stationsByEvent = useMemo(() => {
    const map = new Map<number, XkStation[]>();
    for (const s of stationsQ.data?.items ?? []) {
      const list = map.get(s.eventId) ?? [];
      list.push(s);
      map.set(s.eventId, list);
    }
    return map;
  }, [stationsQ.data]);

  if (!brandId) return null;

  const brand = brandQ.data ?? null;
  const projects = groupsQ.data?.items ?? [];

  // Collapsed sidebar — just the brand logo as the anchor; expanded tree is
  // hidden because there's no room. Click navigates to brand profile.
  if (!expanded) {
    return (
      <div className="my-2 flex justify-center">
        <Link
          to={`/brand/${brandId}`}
          title={brand?.name}
          className={cn(
            'grid h-9 w-9 place-items-center rounded-lg border transition-colors',
            location.pathname === `/brand/${brandId}`
              ? 'border-[var(--color-chrome-accent)] bg-[var(--color-chrome-accent-soft)]'
              : 'border-[var(--color-chrome-border)] hover:border-[var(--color-chrome-accent)]',
          )}
        >
          {brand?.logo ? (
            <img
              src={resolveImageUrl(brand.logo)}
              alt={brand.name ?? ''}
              className="h-6 w-6 rounded object-cover"
            />
          ) : (
            <Diamond className="h-4 w-4 text-[var(--color-chrome-accent-ink)]" />
          )}
        </Link>
      </div>
    );
  }

  return (
    <div className="mb-2 mt-1 rounded-lg border border-[var(--color-chrome-border)] bg-[var(--color-chrome-bg-elevated)] p-1.5">
      {/* Brand header — clickable, navigates to /brand/:id */}
      <Link
        to={`/brand/${brandId}`}
        className={cn(
          'flex items-center gap-2 rounded-md px-2 py-1.5 transition-colors',
          location.pathname === `/brand/${brandId}`
            ? 'bg-[var(--color-chrome-accent-soft)] text-[var(--color-chrome-accent-ink)]'
            : 'text-[var(--color-chrome-fg)] hover:bg-[var(--color-chrome-bg)]',
        )}
      >
        {brand?.logo ? (
          <img
            src={resolveImageUrl(brand.logo)}
            alt=""
            className="h-5 w-5 flex-none rounded object-cover"
          />
        ) : (
          <Diamond className="h-4 w-4 flex-none text-[var(--color-chrome-accent-ink)]" />
        )}
        <span className="min-w-0 flex-1 truncate text-[12px] font-extrabold uppercase tracking-wider">
          {brand?.name ?? '—'}
        </span>
      </Link>

      {/* Projects accordion */}
      {groupsQ.isLoading ? (
        <div className="px-2 py-2 text-[10px] text-[var(--color-chrome-fg-muted)]">
          {t('common.loading')}
        </div>
      ) : projects.length === 0 ? (
        <div className="px-2 py-2 text-[10px] text-[var(--color-chrome-fg-muted)]">
          {t('sidebar.brandTree.noProjects')}
        </div>
      ) : (
        <ul className="mt-1 flex flex-col gap-0.5">
          {projects.map((p) => {
            const events = eventsByGroup.get(p.id) ?? [];
            const isProjectOpen = expandedProjectId === p.id;
            const isProjectActive = location.pathname === `/projects/${p.id}`;
            return (
              <li key={p.id}>
                <div className="flex items-center">
                  <button
                    type="button"
                    onClick={() =>
                      setExpandedProjectId(isProjectOpen ? null : p.id)
                    }
                    aria-expanded={isProjectOpen}
                    aria-label={
                      isProjectOpen
                        ? t('sidebar.brandTree.collapse')
                        : t('sidebar.brandTree.expand')
                    }
                    className="grid h-6 w-5 flex-none place-items-center rounded text-[var(--color-chrome-fg-muted)] hover:text-[var(--color-chrome-fg)]"
                  >
                    <ChevronRight
                      className={cn(
                        'h-3 w-3 transition-transform',
                        isProjectOpen && 'rotate-90',
                      )}
                    />
                  </button>
                  <Link
                    to={`/projects/${p.id}`}
                    title={p.name}
                    className={cn(
                      'flex min-w-0 flex-1 items-center gap-1.5 rounded-md px-1.5 py-1 text-[11px] font-semibold transition-colors',
                      isProjectActive
                        ? 'bg-[var(--color-chrome-accent-soft)] text-[var(--color-chrome-accent-ink)]'
                        : 'text-[var(--color-chrome-fg)] hover:bg-[var(--color-chrome-bg)]',
                    )}
                  >
                    <FolderOpen className="h-3 w-3 flex-none" />
                    <span className="min-w-0 flex-1 truncate">{p.name}</span>
                  </Link>
                </div>
                {isProjectOpen && (
                  <ul className="ml-4 mt-0.5 flex flex-col gap-0.5 border-l border-[var(--color-chrome-border)] pl-2">
                    {events.length === 0 ? (
                      <li className="px-1.5 py-1 text-[10px] text-[var(--color-chrome-fg-muted)]">
                        {t('sidebar.brandTree.noEvents')}
                      </li>
                    ) : (
                      events.map((e) => {
                        const isEventOpen = expandedEventId === e.id;
                        const isEventActive =
                          location.pathname ===
                          `/projects/${p.id}/events/${e.id}`;
                        const stations = stationsByEvent.get(e.id) ?? [];
                        return (
                          <li key={e.id}>
                            <div className="flex items-center">
                              <button
                                type="button"
                                onClick={() =>
                                  setExpandedEventId(isEventOpen ? null : e.id)
                                }
                                aria-expanded={isEventOpen}
                                aria-label={
                                  isEventOpen
                                    ? t('sidebar.brandTree.collapse')
                                    : t('sidebar.brandTree.expand')
                                }
                                className="grid h-6 w-5 flex-none place-items-center rounded text-[var(--color-chrome-fg-muted)] hover:text-[var(--color-chrome-fg)]"
                              >
                                <ChevronRight
                                  className={cn(
                                    'h-3 w-3 transition-transform',
                                    isEventOpen && 'rotate-90',
                                  )}
                                />
                              </button>
                              <Link
                                to={`/projects/${p.id}/events/${e.id}`}
                                title={e.name}
                                className={cn(
                                  'flex min-w-0 flex-1 items-center gap-1.5 rounded-md px-1.5 py-1 text-[11px] transition-colors',
                                  isEventActive
                                    ? 'bg-[var(--color-chrome-accent-soft)] text-[var(--color-chrome-accent-ink)]'
                                    : 'text-[var(--color-chrome-fg)] hover:bg-[var(--color-chrome-bg)]',
                                )}
                              >
                                <Calendar className="h-3 w-3 flex-none" />
                                <span className="min-w-0 flex-1 truncate">
                                  {e.name}
                                </span>
                              </Link>
                            </div>
                            {isEventOpen && (
                              <ul className="ml-4 mt-0.5 flex flex-col gap-0.5 border-l border-[var(--color-chrome-border)] pl-2">
                                {stations.length === 0 ? (
                                  <li className="px-1.5 py-1 text-[10px] text-[var(--color-chrome-fg-muted)]">
                                    {t('sidebar.brandTree.noStations')}
                                  </li>
                                ) : (
                                  stations.map((st) => {
                                    const isStationActive =
                                      location.pathname ===
                                      `/stations/${st.id}`;
                                    return (
                                      <li key={st.id}>
                                        {/* Stations are leaf nodes — no
                                            chevron, no expand affordance.
                                            The leading spacer keeps the
                                            station icon aligned with the
                                            event row above. */}
                                        <div className="flex items-center">
                                          <span
                                            className="h-6 w-5 flex-none"
                                            aria-hidden
                                          />
                                          <Link
                                            to={`/stations/${st.id}`}
                                            title={st.name}
                                            className={cn(
                                              'flex min-w-0 flex-1 items-center gap-1.5 rounded-md px-1.5 py-1 text-[11px] transition-colors',
                                              isStationActive
                                                ? 'bg-[var(--color-chrome-accent-soft)] text-[var(--color-chrome-accent-ink)]'
                                                : 'text-[var(--color-chrome-fg)] hover:bg-[var(--color-chrome-bg)]',
                                            )}
                                          >
                                            <MapPin className="h-3 w-3 flex-none" />
                                            <span className="min-w-0 flex-1 truncate">
                                              {st.name}
                                            </span>
                                          </Link>
                                        </div>
                                      </li>
                                    );
                                  })
                                )}
                              </ul>
                            )}
                          </li>
                        );
                      })
                    )}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
